/**
 * Condition Template Admin
 *
 * Admin-only view of condition document templates and their linked conditions.
 * Read-only interface to see which templates are available for conditions.
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Loader2, FileText, Download, CheckCircle2, XCircle, Zap, Trash2 } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { conditionTemplateService } from '@/services/conditionTemplateService';
import { conditionClearingService } from '@/services/conditionClearingService';
import { CleanApprovalConditionsAdmin } from './CleanApprovalConditionsAdmin';
import type { ConditionTemplate } from '@/types/condition-templates';
import type { ConditionAutoClearRule } from '@/types/condition-clearing';
import { createComponentLogger } from '@/utils/logger';

const log = createComponentLogger('ConditionTemplateAdmin', 'admin');

export const ConditionTemplateAdmin: React.FC = () => {
  const [templates, setTemplates] = useState<ConditionTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  // Auto-clear rules state
  const [rules, setRules] = useState<ConditionAutoClearRule[]>([]);
  const [rulesLoading, setRulesLoading] = useState(true);
  const [rulesError, setRulesError] = useState<string | null>(null);
  const [togglingRuleId, setTogglingRuleId] = useState<string | null>(null);
  const [pendingDeleteRule, setPendingDeleteRule] = useState<ConditionAutoClearRule | null>(null);
  const [deletingRule, setDeletingRule] = useState(false);

  useEffect(() => {
    fetchTemplates();
    fetchRules();
  }, []);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await conditionTemplateService.getActiveTemplates();
      setTemplates(data);
      log.info('Loaded condition templates', { count: data.length });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load templates';
      setError(message);
      log.error('Failed to load templates', {}, err);
    } finally {
      setLoading(false);
    }
  };

  const fetchRules = async () => {
    try {
      setRulesLoading(true);
      setRulesError(null);
      const data = await conditionClearingService.getRules();
      setRules(data);
      log.info('Loaded auto-clear rules', { count: data.length });
    } catch (err) {
      setRulesError(err instanceof Error ? err.message : 'Failed to load auto-clear rules');
      log.error('Failed to load auto-clear rules', {}, err);
    } finally {
      setRulesLoading(false);
    }
  };

  const handleToggleRule = async (rule: ConditionAutoClearRule) => {
    try {
      setTogglingRuleId(rule.id);
      await conditionClearingService.toggleRule(rule.id, !rule.isActive);
      setRules(prev => prev.map(r =>
        r.id === rule.id ? { ...r, isActive: !r.isActive } : r
      ));
      log.info('Toggled rule', { ruleId: rule.id, isActive: !rule.isActive });
    } catch (err) {
      log.error('Failed to toggle rule', { ruleId: rule.id }, err);
    } finally {
      setTogglingRuleId(null);
    }
  };

  const handleToggleAutoMode = async (rule: ConditionAutoClearRule) => {
    try {
      setTogglingRuleId(rule.id);
      const newValue = !rule.requireConfirmation;
      await conditionClearingService.updateRule(rule.id, { requireConfirmation: newValue });
      setRules(prev => prev.map(r =>
        r.id === rule.id ? { ...r, requireConfirmation: newValue } : r
      ));
      log.info('Toggled auto mode', { ruleId: rule.id, requireConfirmation: newValue });
    } catch (err) {
      log.error('Failed to toggle auto mode', { ruleId: rule.id }, err);
    } finally {
      setTogglingRuleId(null);
    }
  };

  const handleDeleteRule = async () => {
    if (!pendingDeleteRule || deletingRule) {
      return;
    }
    const rule = pendingDeleteRule;
    try {
      setDeletingRule(true);
      await conditionClearingService.deleteRule(rule.id);
      setRules(prev => prev.filter(r => r.id !== rule.id));
      setPendingDeleteRule(null);
      log.info('Deleted rule', { ruleId: rule.id });
    } catch (err) {
      log.error('Failed to delete rule', { ruleId: rule.id }, err);
    } finally {
      setDeletingRule(false);
    }
  };

  const handleDownloadTemplate = async (template: ConditionTemplate) => {
    try {
      setDownloadingId(template.id);
      const url = await conditionTemplateService.getTemplateDownloadUrl(template.storagePath);

      // Open in new tab to preview/download
      window.open(url, '_blank');
      log.info('Downloaded template', { templateId: template.id });
    } catch (err) {
      log.error('Failed to download template', { templateId: template.id }, err);
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Condition Automations</h1>
          <p className="text-muted-foreground">
            Manage condition documents, clearing behavior, and Clean Approval learning
          </p>
        </div>
        <Badge variant="secondary" className="text-sm">
          {templates.length} Template{templates.length !== 1 ? 's' : ''}
        </Badge>
      </div>

      <Tabs defaultValue="document-templates" className="space-y-6">
        <TabsList className="grid h-auto w-full grid-cols-1 gap-1 sm:w-auto sm:grid-cols-3">
          <TabsTrigger value="document-templates">Document Templates</TabsTrigger>
          <TabsTrigger value="auto-clear-rules">Auto-Clear Rules</TabsTrigger>
          <TabsTrigger value="clean-approval">Clean Approval Conditions</TabsTrigger>
        </TabsList>

        <TabsContent value="document-templates" className="space-y-6">
          {loading ? (
            <div className="flex h-64 items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : error ? (
            <Card className="border-destructive">
              <CardHeader>
                <CardTitle className="text-destructive">Error Loading Templates</CardTitle>
                <CardDescription>{error}</CardDescription>
              </CardHeader>
              <CardContent>
                <Button onClick={fetchTemplates} variant="outline">Retry</Button>
              </CardContent>
            </Card>
          ) : (
            <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Template Library
          </CardTitle>
          <CardDescription>
            Templates are automatically merged with loan data when borrowers download condition documents
          </CardDescription>
        </CardHeader>
        <CardContent>
          {templates.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No condition templates configured</p>
              <p className="text-sm mt-1">
                Templates are added via database and storage upload
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[100px]">External ID</TableHead>
                  <TableHead>Condition Title</TableHead>
                  <TableHead>Template Name</TableHead>
                  <TableHead className="w-[100px]">Status</TableHead>
                  <TableHead className="w-[100px] text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {templates.map((template) => (
                  <TableRow key={template.id}>
                    <TableCell className="font-mono text-sm">
                      {template.externalId}
                    </TableCell>
                    <TableCell className="font-medium">
                      {template.conditionTitle}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {template.templateName}
                    </TableCell>
                    <TableCell>
                      {template.isActive ? (
                        <Badge variant="default" className="gap-1">
                          <CheckCircle2 className="h-3 w-3" />
                          Active
                        </Badge>
                      ) : (
                        <Badge variant="secondary" className="gap-1">
                          <XCircle className="h-3 w-3" />
                          Inactive
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDownloadTemplate(template)}
                        disabled={downloadingId === template.id}
                      >
                        {downloadingId === template.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Download className="h-4 w-4" />
                        )}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Available Merge Fields</CardTitle>
          <CardDescription>
            These fields are automatically populated from loan data when generating documents
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
            {[
              'loan_number',
              'lender_name',
              'borrower_name',
              'borrower_first_name',
              'borrower_last_name',
              'coborrower_name',
              'coborrower_first_name',
              'coborrower_last_name',
              'property_address',
              'property_city',
              'property_state',
              'property_zip',
              'property_full_address',
              'sig_borrower_name',
              'sig_coborrower_name'
            ].map((field) => (
              <code
                key={field}
                className="text-xs bg-muted px-2 py-1 rounded font-mono"
              >
                {field}
              </code>
            ))}
          </div>
        </CardContent>
      </Card>
            </>
          )}
        </TabsContent>

      {/* Auto-Clear Rules Section */}
      <TabsContent value="auto-clear-rules">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5 text-emerald-600" />
            Auto-Clear Rules
          </CardTitle>
          <CardDescription>
            Rules that match eFolder documents to conditions for clearing.
            <strong>Active</strong> enables the rule. <strong>Auto</strong> clears without confirmation — when off, matches are logged but not auto-cleared.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {rulesLoading ? (
            <div className="flex items-center justify-center h-24">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : rulesError ? (
            <div className="space-y-3 py-6 text-center">
              <p className="text-sm text-destructive">{rulesError}</p>
              <Button variant="outline" size="sm" onClick={fetchRules}>Retry</Button>
            </div>
          ) : rules.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Zap className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No auto-clear rules configured</p>
              <p className="text-sm mt-1">
                Rules are added via database. Contact admin to add new rules.
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Rule Name</TableHead>
                  <TableHead>Condition Match</TableHead>
                  <TableHead>Document Match</TableHead>
                  <TableHead className="w-[80px]">Action</TableHead>
                  <TableHead className="w-[90px]">Disclosure</TableHead>
                  <TableHead className="w-[80px]">Active</TableHead>
                  <TableHead className="w-[80px]">Auto</TableHead>
                  <TableHead className="w-[60px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rules.map((rule) => (
                  <TableRow key={rule.id}>
                    <TableCell>
                      <div>
                        <p className="font-medium text-sm">{rule.name}</p>
                        {rule.description && (
                          <p className="text-xs text-muted-foreground mt-0.5">{rule.description}</p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        <Badge variant="outline" className="text-xs mr-1">
                          {rule.conditionMatchField}
                        </Badge>
                        <Badge variant="secondary" className="text-xs mr-1">
                          {rule.conditionMatchStrategy}
                        </Badge>
                        <code className="text-xs bg-muted px-1 rounded">
                          {rule.conditionMatchPattern}
                        </code>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        <Badge variant="secondary" className="text-xs mr-1">
                          {rule.documentMatchStrategy}
                        </Badge>
                        <code className="text-xs bg-muted px-1 rounded">
                          {rule.documentMatchPattern}
                        </code>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={rule.action === 'clear' ? 'default' : 'secondary'} className="text-xs capitalize">
                        {rule.action}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {rule.disclosureCheckEnabled ? (
                        <div className="space-y-0.5">
                          <Badge variant="default" className="text-xs bg-emerald-600">
                            {rule.disclosureTypeRequired || 'Yes'}
                          </Badge>
                          {rule.disclosureEventFieldName && (
                            <p className="text-[10px] text-muted-foreground">
                              {rule.disclosureEventFieldName} {rule.disclosureEventFieldOperator}
                            </p>
                          )}
                        </div>
                      ) : (
                        <Badge variant="secondary" className="text-xs">
                          Off
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={rule.isActive}
                        onCheckedChange={() => handleToggleRule(rule)}
                        disabled={togglingRuleId === rule.id}
                      />
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={!rule.requireConfirmation}
                        onCheckedChange={() => handleToggleAutoMode(rule)}
                        disabled={togglingRuleId === rule.id || !rule.isActive}
                      />
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setPendingDeleteRule(rule)}
                        className="text-destructive hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
      </TabsContent>

      <TabsContent value="clean-approval">
        <CleanApprovalConditionsAdmin />
      </TabsContent>
      </Tabs>

      <AlertDialog open={pendingDeleteRule !== null} onOpenChange={(open) => {
        if (!open && !deletingRule) {
          setPendingDeleteRule(null);
        }
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete auto-clear rule?</AlertDialogTitle>
            <AlertDialogDescription>
              Delete “{pendingDeleteRule?.name}”? This permanently removes the matching and clearing behavior for future documents.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deletingRule}>Cancel</AlertDialogCancel>
            <Button variant="destructive" onClick={() => void handleDeleteRule()} disabled={deletingRule}>
              {deletingRule && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Delete rule
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ConditionTemplateAdmin;
